/* assets/hs-eyebrow-reveal.js
   Site-wide, one-time scroll reveal for every "eyebrow pill" badge
   (snippets/hs-eyebrow-reveal.liquid) — see that snippet's own header
   comment for the full derivation against shed.design's real
   .title-pill--small label reveal. Loaded once in layout/theme.liquid,
   not inside the snippet itself, because the snippet is rendered by
   several different sections on the same page.

   History (all 2026-10-13, same day, three passes — read before touching
   this file again, each pass fixed a real, confirmed bug, not a guess):
   1. IntersectionObserver alone: unreliable on a real phone outside the
      Instagram in-app browser ("барely moved and not all of them").
      Added a scroll/resize-driven fallback (getBoundingClientRect vs
      window.innerHeight) alongside it.
   2. That fallback called reveal() SYNCHRONOUSLY, so any pill already
      satisfying the condition at the moment it ran (very common — the
      initial on-load check, or just a lot of content sitting within 90%
      of viewport height) never got its hidden rest state painted even
      once before flipping to visible: nothing to transition FROM, so no
      animation played, just an instant snap to the end state. Confirmed
      live: only the ONE pill a real visit actually had to scroll to
      (revealed later, after its rest state had been on screen for
      several frames) animated; every other one was static from first
      paint. Fixed by deferring the class toggle through a double
      requestAnimationFrame (+ setTimeout belt-and-suspenders), matching
      hs-shed-hero/hs-cat__title's own established pattern.
   3. STILL reported broken after pass 2 on a real phone — "деякі ледь
      рухаються, інші не спрацьовують взагалі" (some barely move, others
      don't fire at all) — inconsistent per-pill, not explained by either
      earlier fix. A same-session timing check (MutationObserver vs a real
      scroll) measured CORRECT ~38ms deferred behavior for the exact two
      flagged elements, right off the live theme file — meaning the code
      was provably right in that one test, yet still failed for the
      client. Rather than chase one more single hypothesis blind a third
      time, this pass removes the speculative, UNCONFIRMED
      `transform: translateZ(0)` "Safari clipping fix" from
      component-hs-eyebrow-reveal.css (added defensively in pass 1, never
      actually verified to help — forcing a compositing layer on small
      animated text is also a known source of blurry/jittery rendering in
      Safari, which would read exactly as "barely moves") and adds a
      third, independent trigger: a plain `setInterval` poll. This doesn't
      depend on IntersectionObserver, on 'scroll'/'resize' firing at the
      right moments, or on any scroll-physics library (Lenis) interaction
      — it just asks "is anything still pending, and is it on screen?"
      every 200ms, unconditionally, for as long as anything is pending.
      Cheap (the loop is a no-op the instant nothing is left pending) and
      removes any remaining dependency on a specific browser event timing
      quirk being the (still unconfirmed) cause.
*/
(function () {
  if (window.HSEyebrowRevealInit) return;
  window.HSEyebrowRevealInit = true;

  function reveal(item) {
    function show() {
      item.classList.add('hs-eyebrow-reveal--visible');
    }
    if (document.visibilityState === 'hidden') {
      // rAF never fires on a hidden/background tab — verified elsewhere in
      // this project (header-utility-watch.js, hs-shed-hero, hs-cat__title).
      show();
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(show);
    });
    // Belt-and-suspenders: guarantee the reveal even if rAF is starved.
    setTimeout(show, 100);
  }

  function pending() {
    return document.querySelectorAll('[data-hs-eyebrow-reveal]:not(.hs-eyebrow-reveal--visible)');
  }

  function isNear(item, vh) {
    var rect = item.getBoundingClientRect();
    return rect.top < vh * 0.9 && rect.bottom > 0;
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      });
      // 20% of the element in view — same threshold shed's own reveal
      // uses in spirit; rootMargin isn't measurable from a static read
      // (JS-only, not in any stylesheet), so this stays this project's
      // own reasonable choice, same honesty standard as the snippet's
      // own header comment already flags for the threshold value.
    }, { threshold: 0.2 });
  }

  // Scroll/resize-driven check — independent of IntersectionObserver.
  var ticking = false;
  function checkNow() {
    var items = pending();
    if (!items.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    items.forEach(function (item) {
      if (isNear(item, vh)) {
        reveal(item);
        if (observer) observer.unobserve(item);
      }
    });
  }
  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      checkNow();
      ticking = false;
    });
  }

  // Third, independent trigger — a plain poll, no dependency on any
  // specific event or API actually firing. Self-stops once nothing is
  // left pending; restarts (via init(), below) whenever new pills enter
  // the DOM (Theme Editor section reloads).
  var pollId = null;
  function startPoll() {
    if (pollId) return;
    pollId = setInterval(function () {
      if (!pending().length) {
        clearInterval(pollId);
        pollId = null;
        return;
      }
      checkNow();
    }, 200);
  }

  function init() {
    var items = pending();
    if (!items.length) return;
    items.forEach(function (item) {
      if (observer) observer.observe(item);
    });
    // Covers items already on screen at load (observer also reports
    // these immediately, so this is belt-and-suspenders, not a race) and
    // is the only path at all when IntersectionObserver is unsupported.
    checkNow();
    startPoll();
  }

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor re-renders a section via the Section Rendering API (AJAX
  // HTML swap, no full page reload) — re-scan for any new pill instances
  // each time, same pattern already used by every other scroll/interaction
  // script in this project (e.g. hs-shed-collection-sectors.liquid).
  document.addEventListener('shopify:section:load', init);
})();
